// Single source of truth for turning the Facebook `_fbc`/`_fbp` cookies into
// the Segment options object that Meta's Conversions API destination reads
// (`$.context.fbc` / `$.context.fbp`; `fbc` encodes the fbclid as
// `fb.1.<ts>.<fbclid>`).
//
// Both checkout funnel fires build their fb context from here so they can't
// drift: the pre-React early fire (earlyCheckoutTracking.ts, for LCP/funnel
// timing parity) and the React-side fire (useAnalytics.ts). A drift between
// those two — the early fire omitting this context — previously dropped the
// click ID on `begin_checkout` and broke Meta's Initiate Checkout attribution
// (OMG-1889). Keeping one builder makes that class of bug impossible.

/** Read a cookie value by name from document.cookie (decoded), else undefined. */
export const readCookie = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + name + "=([^;]*)"),
  );
  if (!match) return undefined;
  // A malformed %-sequence (e.g. a "50%off" discount code written raw by a
  // marketing tool) makes decodeURIComponent throw URIError. Callers run at
  // module scope in main.tsx BEFORE React mounts, so a throw here would
  // white-screen the page — fall back to the raw value instead.
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

/**
 * Segment track/page options carrying the Facebook click + browser IDs in
 * `context`. Returns undefined when neither cookie is present, so non-ad
 * traffic sends no fb context (rather than an empty object).
 */
export const fbCapiContext = ():
  | { context: { fbc?: string; fbp?: string } }
  | undefined => {
  const fbc = readCookie("_fbc");
  const fbp = readCookie("_fbp");
  if (!fbc && !fbp) return undefined;
  return {
    context: {
      ...(fbc ? { fbc } : {}),
      ...(fbp ? { fbp } : {}),
    },
  };
};
